/**
 * The ONE place that touches the Firebase SDK. (ARCHITECTURE.md §3)
 *
 * No feature module imports `firebase/*` directly. When the SDK changes, this
 * file changes and nothing else does. Just as importantly, every feature gets
 * the same answer to "am I signed in, and is the database ready" instead of
 * each one guessing.
 *
 * The legacy file called `window._fb.setDoc(...)` from 40-odd places, with a
 * different set of null-checks at each call site. That is why a signed-out
 * user could reach code paths that assumed a uid.
 *
 * The sign-in half lives in `./firebase-auth.js` and is re-exported from the
 * bottom of this file, so this module's public surface is unchanged. It was
 * split out on 16 Aug 2026 when this file hit the 400-line guard limit.
 */

import { initializeApp } from 'firebase/app'
import {
  getFirestore,
  initializeFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  deleteField,
  writeBatch,
  onSnapshot,
  query,
  where,
  increment,
  serverTimestamp,
} from 'firebase/firestore'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { createLatest } from './latest.js'
import { cacheSettings } from './firestore-cache.js'
import { bindAuth } from './firebase-auth.js'
import { bindStorage } from './firebase-storage.js'
import { watchWithRetry } from './watch-retry.js'

/** Cloud Functions region. Declared once — the legacy file declares it twice
 *  in two separate closures, which is exactly the collision this prevents. */
export const FN_REGION = 'us-central1'

/**
 * Config comes from the environment, never from source. See `.env.example`.
 * Firebase web config is not secret, but hard-coding it makes staging and
 * production the same environment, which is how every deploy became a live
 * experiment. (ARCHITECTURE.md §8)
 */
const CONFIG = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

/**
 * ── DO NOT "FIX" THE FIRESTORE TRANSPORT WARNINGS IN SAFARI ───────────────
 *
 * Safari logs `Fetch API cannot load .../Firestore/Listen/channel ... due to
 * access control checks` on every load, here and on `/app`. Diagnosed 15 Aug
 * 2026: `DEFAULT_AUTO_DETECT_LONG_POLLING` is `true` in firebase 10.14.1, so
 * the SDK probes WebChannel, watches Safari block it, and falls back to long
 * polling. **The errors are the probe.** Proof the fallback establishes: the
 * Account screen renders its trip count from an `onSnapshot` listener, and
 * that number appears in Safari.
 *
 * `experimentalForceLongPolling: true` would silence it by putting EVERY
 * browser on the slower transport to quiet a message on one. Deliberately
 * not set. Full reasoning and the URLs: HANDOFF.md §7.
 */

let app = null
let db = null
let auth = null
let fns = null
let currentUser = null

/** Whether the offline copy is in IndexedDB (true) or memory only (false). */
let cachePersistent = false

/**
 * Auth state is published through a "latest value" registry so that a
 * subscriber which registers AFTER Firebase's first callback still receives
 * it. Without that, awaiting anything between initFirebase() and onUser()
 * left the app stuck on its splash screen forever. See lib/latest.js.
 */
const userState = createLatest()

/**
 * Open Firestore with the offline copy in IndexedDB rather than in memory.
 *
 * Firebase owns the offline copy from here on; nothing hand-writes trips into
 * localStorage. Falls back to memory when IndexedDB is unavailable (private
 * browsing), because that must not stop the app loading. Which cache, and why
 * Safari gets a different one: `lib/firestore-cache.js`. Measured numbers:
 * `docs/DATA_MODEL.md`.
 *
 * @param {any} firebaseApp
 * @returns {any} the Firestore instance
 */
function openFirestore(firebaseApp) {
  try {
    const instance = initializeFirestore(firebaseApp, { localCache: cacheSettings() })
    cachePersistent = true
    return instance
  } catch (err) {
    console.warn('persistent Firestore cache unavailable; using memory only', err)
    cachePersistent = false
    return getFirestore(firebaseApp)
  }
}

/**
 * Boot Firebase. Safe to call more than once.
 * @returns {Promise<{ ok: boolean, reason?: string }>}
 */
export async function initFirebase() {
  if (app) return { ok: true }

  if (!CONFIG.apiKey || !CONFIG.projectId) {
    return { ok: false, reason: 'Firebase config missing. Copy .env.example to .env.local.' }
  }

  try {
    app = initializeApp(CONFIG)
    db = openFirestore(app)
    auth = getAuth(app)
    bindAuth(auth)
    bindStorage(app)
    fns = getFunctions(app, FN_REGION)

    onAuthStateChanged(auth, (user) => {
      currentUser = user || null
      userState.publish(currentUser)
    })

    return { ok: true }
  } catch (err) {
    app = null
    return { ok: false, reason: err instanceof Error ? err.message : String(err) }
  }
}

/**
 * Whether the offline copy survives a reload. `false` means every read after
 * a refresh costs quota; a screen may want to be less chatty in that case.
 * @returns {boolean}
 */
export function hasPersistentCache() {
  return cachePersistent
}

/** @returns {any} the signed-in user, or null */
export function getUser() {
  return currentUser
}

/** @returns {string} the signed-in uid, or '' */
export function getUid() {
  return currentUser ? currentUser.uid : ''
}

/** @returns {boolean} */
export function isReady() {
  return Boolean(db)
}

/**
 * Subscribe to sign-in / sign-out. Returns an unsubscribe function.
 * @param {(user: any) => void} listener
 * @returns {() => void}
 */
export function onUser(listener) {
  return userState.subscribe(listener)
}

/**
 * Whether Firebase has reported an auth state at least once. `false` means
 * "we do not know yet", which is different from "signed out".
 * @returns {boolean}
 */
export function isAuthResolved() {
  return userState.hasValue()
}

/* ── Firestore ───────────────────────────────────────────────────────────── */

function requireDb() {
  if (!db) throw new Error('Firestore is not ready. Await initFirebase() first.')
  return db
}

/**
 * @param {string[]} path - e.g. ['forms', uid, 'leads', leadId]
 */
export function docRef(path) {
  if (!path.length) throw new Error('docRef requires a path')
  // Split rather than spread: the SDK types the first segment separately, and
  // a bare spread silently accepts an empty path.
  return doc(requireDb(), path[0], ...path.slice(1))
}

/**
 * @param {string[]} path - e.g. ['forms', uid, 'leads']
 */
export function colRef(path) {
  if (!path.length) throw new Error('colRef requires a path')
  return collection(requireDb(), path[0], ...path.slice(1))
}

/**
 * Read one document.
 * @param {string[]} path
 * @returns {Promise<any|null>} the data, or null when the document is absent
 */
export async function readDoc(path) {
  const snap = await getDoc(docRef(path))
  return snap.exists() ? snap.data() : null
}

/**
 * Read every document in a collection as `{ id, ...data }`.
 * @param {string[]} path
 * @returns {Promise<any[]>}
 */
export async function readCollection(path) {
  const snap = await getDocs(colRef(path))
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }))
}

/**
 * Overwrite a document.
 * @param {string[]} path
 * @param {Record<string, any>} data
 */
export function writeDoc(path, data) {
  return setDoc(docRef(path), data)
}

/**
 * Patch a document, leaving unlisted fields alone.
 * @param {string[]} path
 * @param {Record<string, any>} patch
 */
export function mergeDoc(path, patch) {
  return setDoc(docRef(path), patch, { merge: true })
}

/**
 * @param {string[]} path
 */
export function removeDoc(path) {
  return deleteDoc(docRef(path))
}

/**
 * Apply several writes as one unit — all of them land, or none do. A trip is
 * saved as a document AND as an entry in the summary index; half of that pair
 * is a trip missing from every list, or a list entry pointing at nothing.
 *
 * @param {Array<{op: 'set'|'merge'|'delete', path: string[], data?: Record<string, any>}>} ops
 * @returns {Promise<void>}
 */
export function writeBatched(ops) {
  const batch = writeBatch(requireDb())
  for (const entry of ops) {
    const ref = docRef(entry.path)
    if (entry.op === 'delete') batch.delete(ref)
    else if (entry.op === 'merge') batch.set(ref, entry.data || {}, { merge: true })
    else batch.set(ref, entry.data || {})
  }
  return batch.commit()
}

/* The only Firestore VALUES a feature needs. Importing them from
   `firebase/firestore` inside a feature would be the second place the SDK is
   touched, and that rule survives only while nobody makes a small exception.
   A browser-stamped event carries a clock the advisor can set; and
   read-modify-write on a counter drops events when two tabs are open. */

/** The server's clock, as a value to write. @returns {any} */
export const serverTime = () => serverTimestamp()

/** An atomic counter step, applied server-side. @param {number} [by] @returns {any} */
export const countUp = (by = 1) => increment(Number.isFinite(by) ? Number(by) : 1)

/** Remove one key from a document during a merge. @returns {any} */
export const removeField = () => deleteField()

/**
 * Watch a collection. Returns an unsubscribe function.
 *
 * @param {string[]} path
 * @param {(docs: {id: string, data: any}[]) => void} onChange
 * @param {(err: Error) => void} [onError]
 * @returns {() => void}
 */
export function watchCollection(path, onChange, onError) {
  /* One retry if the first answer is a refusal that might be early — see
     `watch-retry.js`. A brand-new account's first reads all land before its
     token does. */
  return watchWithRetry(
    (fail) =>
      onSnapshot(colRef(path), (s) => onChange(s.docs.map((d) => ({ id: d.id, data: d.data() }))), fail),
    (err) => {
      console.error('watchCollection failed', path.join('/'), err)
      if (onError) onError(err)
    }
  )
}

/**
 * Watch a single document — the cheapest live view Firestore offers: after
 * the first read it costs a read only when that document changes.
 *
 * @param {string[]} path
 * @param {(data: any|null) => void} onChange
 * @param {(err: Error) => void} [onError]
 * @returns {() => void}
 */
export function watchDoc(path, onChange, onError) {
  return watchWithRetry(
    (fail) => onSnapshot(docRef(path), (snap) => onChange(snap.exists() ? snap.data() : null), fail),
    (err) => {
      console.error('watchDoc failed', path.join('/'), err)
      if (onError) onError(err)
    }
  )
}

/**
 * Watch a filtered collection.
 * @param {string[]} path
 * @param {[string, any, any]} filter - [field, op, value]
 * @param {(docs: {id: string, data: any}[]) => void} onChange
 * @returns {() => void}
 */
export function watchWhere(path, filter, onChange) {
  const [field, op, value] = filter
  return onSnapshot(query(colRef(path), where(field, op, value)), (snapshot) =>
    onChange(snapshot.docs.map((d) => ({ id: d.id, data: d.data() })))
  )
}

/* ── Cloud Functions ─────────────────────────────────────────────────────── */

/**
 * Call a Cloud Function by name.
 * @param {string} name
 * @param {Record<string, any>} [payload]
 * @returns {Promise<any>} the function's `data`
 */
export async function callFunction(name, payload = {}) {
  if (!fns) throw new Error('Functions are not ready. Await initFirebase() first.')
  const result = await httpsCallable(fns, name)(payload)
  return result.data
}

/* ── Cloud Storage ───────────────────────────────────────────────────────── */

/* Same arrangement as auth: `./firebase-storage.js` holds the bodies and this
   list keeps `lib/firebase.js` the single import path. Storage needs Blaze;
   the project is on it (HANDOFF §4). */
export { isStorageReady, uploadBytesTo, deleteStoredObject } from './firebase-storage.js'

/* ── Auth ────────────────────────────────────────────────────────────────── */

/* Re-exported, not reimplemented. `./firebase-auth.js` holds the bodies; this
   list keeps `lib/firebase.js` the single import path every feature already
   uses, so the split cost zero changes anywhere else. */
export {
  signIn,
  signUp,
  signInWithGoogle,
  signInWithGoogleRedirect,
  signOut,
  resetPassword,
  consumeRedirectResult,
  sendVerificationEmail,
  setDisplayName,
  hasPasswordProvider,
  changePassword,
  reauthenticate,
  tokenReady,
  refreshToken,
} from './firebase-auth.js'
